import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { getSessionId } from '@onefootprint/dev-tools';
import isPlainObject from 'lodash/isPlainObject';
import { ENABLE_DDOG_LOGS_ON_CONSOLE, IS_LOGGING_ENABLED } from './constants';
import type { ExtraProps, PrimitiveData } from './types';
import {
  dataDogAction,
  dataDogErrorEvent,
  dataDogInfoEvent,
  dataDogTrackEvent,
  dataDogWarnEvent,
  initDataDogLogs,
  initDataDogRum,
} from './utils/datadog';
import { getDeviceMemory, getDeviceNetwork } from './utils/get-device-info';
import getEnvInfo from './utils/get-env-info';

const FALLBACK_ERROR_MESSAGE = 'Something went wrong';

const getErrorMessage = (error?: unknown): string => {
  if (typeof error === 'string') return error;
  if ((error as Error)?.message) return (error as Error).message;
  return FALLBACK_ERROR_MESSAGE;
};

type ErrorLike = {
  name?: unknown;
  code?: unknown;
  requestPath?: unknown;
  response?: {
    status?: unknown;
  };
};

// Request/library errors can include circular objects, so keep only simple fields for logging
const getErrorMetadata = (error?: unknown): PrimitiveData => {
  const requestError = error as ErrorLike | undefined;

  return {
    errorName: typeof requestError?.name === 'string' ? requestError.name : undefined,
    errorCode: typeof requestError?.code === 'string' ? requestError.code : undefined,
    requestPath: typeof requestError?.requestPath === 'string' ? requestError.requestPath : undefined,
    errorStatus: typeof requestError?.response?.status === 'number' ? requestError.response.status : undefined,
  };
};

/**
 * Conditions the product already handles on screen, logged as warnings so `status:error` stays a
 * list of things worth paging someone about. E117/E118/E119 also live in `LOGOUT_ERROR_CODES` in
 * `@onefootprint/fetch-hosted`.
 */
const BENIGN_ERROR_CODES = new Set([
  'E102', // Incorrect PIN code
  'E117', // Session does not exist
  'E118', // Session is expired
  'E119', // Session invalid
  'E125', // Link has already been used
  'E138', // Request was cancelled
]);

const BENIGN_ERROR_NAMES = new Set(['AbortError', 'CanceledError', 'NotAllowedError']);

/**
 * The browsers' several ways of saying a request never completed. These arrive as thrown errors
 * with `origin: logger` and no `http.status_code`, so the network gate in `shouldSendLog` can't
 * reach them. A genuine outage still surfaces as a 5xx, which that gate keeps.
 */
const BENIGN_ERROR_MESSAGES = [
  'Failed to fetch', // Chrome
  'Load failed', // Safari
  'Network Error', // axios
  'NetworkError', // Firefox
];

const isBenignError = (error?: unknown): boolean => {
  const { errorCode, errorName } = getErrorMetadata(error);
  const errorMessage = getErrorMessage(error);

  return (
    (typeof errorCode === 'string' && BENIGN_ERROR_CODES.has(errorCode)) ||
    (typeof errorName === 'string' && BENIGN_ERROR_NAMES.has(errorName)) ||
    BENIGN_ERROR_MESSAGES.some(benign => errorMessage.includes(benign))
  );
};

// Forward a plain Error instance to Datadog to avoid deep/circular serialization issues
const getSafeError = (error: unknown, fallbackMessage: string): Error => {
  // getErrorMessage never returns an empty string, it returns the placeholder, so a `||` fallback
  // would never fire.
  const extracted = getErrorMessage(error);
  const safeMessage = extracted === FALLBACK_ERROR_MESSAGE ? fallbackMessage : extracted;
  const safeError = new Error(safeMessage);
  const errorName = (error as ErrorLike | undefined)?.name;

  if (typeof errorName === 'string') {
    safeError.name = errorName;
  }

  return safeError;
};

/**
 * Filters out any traits that are null, undefined, or empty strings.
 *
 * @param traits The object to filter.
 * @returns An object with the same shape as `traits`, but with any null, undefined, or empty string values removed.
 */
const filterNonEmptyTraits = (traits: PrimitiveData): ExtraProps =>
  Object.fromEntries(
    Object.entries(traits).filter(([, value]) => value !== null && value !== undefined && value !== ''),
  );

const LoggerFactory = () => {
  let appName: string = '';
  let hasGlobalContext: boolean = false;
  let isDDLogsEnabled: boolean = false;
  let isDDRumEnabled: boolean = false;
  let isSessionReplayOn: boolean = false;

  const getMessageWithName = (msg: string, extra?: PrimitiveData) => {
    const name = extra?.name;
    return typeof name === 'string' && name ? `[${name.toUpperCase()}] ${msg}` : msg;
  };

  const init = (app: string, deferSessionRecord?: boolean) => {
    if (!IS_LOGGING_ENABLED) {
      return;
    }

    appName = app;
    isDDLogsEnabled = initDataDogLogs(appName);

    if (!deferSessionRecord) {
      isDDRumEnabled = initDataDogRum(appName);
    }

    getEnvInfo().then(setGlobalContext).catch(console.warn);
  };

  /** Set the entire context for all your loggers */
  const setGlobalContext = (traits: PrimitiveData) => {
    if (!IS_LOGGING_ENABLED) {
      return;
    }
    if (!isDDLogsEnabled && !isDDRumEnabled) {
      return;
    }

    const sessionId = getSessionId();
    const ddSessionId = datadogLogs.getInternalContext()?.session_id;
    const contextProps = {
      ...filterNonEmptyTraits(traits),
      ...getDeviceNetwork(),
      ...getDeviceMemory(),
      fp_session_id: sessionId,
    };
    const context = appName === 'bifrost' && ddSessionId ? { ...contextProps, session_id: ddSessionId } : contextProps;

    if (hasGlobalContext) {
      return appendGlobalContext(context);
    }

    datadogLogs.setGlobalContext(context);
    datadogRum.setGlobalContext(context);
    hasGlobalContext = true;
  };

  /** Add a context to all your loggers */
  const appendGlobalContext = (traits: PrimitiveData) => {
    if (!IS_LOGGING_ENABLED) {
      return;
    }
    if (!isDDLogsEnabled && !isDDRumEnabled) {
      return;
    }

    if (isPlainObject(traits)) {
      Object.entries(traits).forEach(([key, value]) => {
        if (key && value !== null && value !== undefined && value !== '') {
          datadogLogs.setGlobalContextProperty(key, value);
          datadogRum.setGlobalContextProperty(key, value);
        }
      });
    }
  };

  /** RUM only — mirroring onto logs would widen where the user's email lands. */
  const setUser = (user: { id: string; name?: string; email?: string }) => {
    if (!IS_LOGGING_ENABLED) {
      return;
    }
    datadogRum.setUser(user);
  };

  const clearUser = () => {
    if (!IS_LOGGING_ENABLED) {
      return;
    }
    datadogRum.clearUser();
  };

  const removeGlobalContext = (keys: string[]) => {
    if (!IS_LOGGING_ENABLED) {
      return;
    }
    keys.forEach(key => {
      datadogLogs.removeGlobalContextProperty(key);
      datadogRum.removeGlobalContextProperty(key);
    });
  };

  const getGlobalContext = (): ReturnType<typeof datadogLogs.getGlobalContext> | undefined => {
    try {
      return datadogLogs.getGlobalContext();
    } catch (_e) {
      return undefined;
    }
  };

  const startSessionReplay = () => {
    if (isSessionReplayOn) {
      return;
    }

    if (!isDDRumEnabled) {
      isDDRumEnabled = initDataDogRum(appName);
    }
    if (isDDRumEnabled) {
      datadogRum.startSessionReplayRecording();
      isSessionReplayOn = true;
    }
  };

  const stopSessionReplay = () => {
    if (!isSessionReplayOn || !isDDRumEnabled) {
      return;
    }
    if (isSessionReplayOn) {
      datadogRum.stopSessionReplayRecording();
      isSessionReplayOn = false;
    }
  };

  const track = (msg: string, extra: PrimitiveData) => {
    const filteredExtra = filterNonEmptyTraits(extra);
    const message = getMessageWithName(msg, extra);

    if (ENABLE_DDOG_LOGS_ON_CONSOLE) {
      console.info('[DD]', message, filteredExtra);
    }
    if (!IS_LOGGING_ENABLED) {
      return;
    }

    if (isDDLogsEnabled) {
      dataDogTrackEvent(message, filteredExtra);
    }
  };

  const error = (err: unknown, extra?: PrimitiveData, msg?: string) => {
    if (isBenignError(err)) {
      warn(msg ?? '', extra, err);
      return;
    }

    const filteredExtra = filterNonEmptyTraits({ ...(extra || {}), ...getErrorMetadata(err) });
    // Drop the placeholder so a call that passes only a message doesn't get
    // "Something went wrong" glued onto it.
    const baseMessage =
      [msg, getErrorMessage(err)].filter(part => !!part && part !== FALLBACK_ERROR_MESSAGE).join(' ') ||
      FALLBACK_ERROR_MESSAGE;
    const errorMessage = getMessageWithName(baseMessage, extra);
    const errorObj = getSafeError(err, errorMessage);
    if (ENABLE_DDOG_LOGS_ON_CONSOLE) {
      console.error('[DD]', errorMessage, filteredExtra, errorObj);
    }
    if (!IS_LOGGING_ENABLED) {
      return;
    }

    if (isDDLogsEnabled) {
      dataDogErrorEvent(errorObj, errorMessage, filteredExtra);
    }
  };

  const warn = (msg: string, extra?: PrimitiveData, err?: unknown) => {
    const filteredExtra = filterNonEmptyTraits({ ...(extra || {}), ...getErrorMetadata(err) });
    const baseMessage = [msg, getErrorMessage(err)]
      .filter(errorMsg => !!errorMsg && errorMsg !== FALLBACK_ERROR_MESSAGE)
      .join(' ');
    const errorMessage = getMessageWithName(baseMessage, extra);
    // A warn with no error attached should carry no stack; a synthetic one only points here.
    const errorObj = err ? getSafeError(err, errorMessage) : undefined;

    if (ENABLE_DDOG_LOGS_ON_CONSOLE) {
      console.warn('[DD]', errorMessage, filteredExtra, errorObj);
    }
    if (!IS_LOGGING_ENABLED) {
      return;
    }

    if (isDDLogsEnabled) {
      dataDogWarnEvent(errorMessage, filteredExtra, errorObj);
    }
  };

  const info = (msg: string, extra?: PrimitiveData) => {
    const filteredExtra = filterNonEmptyTraits(extra || {});
    const message = getMessageWithName(msg, extra);

    if (ENABLE_DDOG_LOGS_ON_CONSOLE) {
      console.info('[DD]', message, filteredExtra);
    }
    if (!IS_LOGGING_ENABLED) {
      return;
    }

    if (isDDLogsEnabled) {
      dataDogInfoEvent(message, filteredExtra);
    }
  };

  return {
    appendGlobalContext,
    clearUser,
    error,
    getGlobalContext,
    info,
    init,
    removeGlobalContext,
    setGlobalContext,
    setUser,
    startSessionReplay,
    stopSessionReplay,
    track,
    warn,
  };
};

const Logger = LoggerFactory();

const getLogger = (
  preExtra: PrimitiveData & { name?: string } = Object.create(null),
): {
  logError: (msg: string, err?: unknown, extra?: PrimitiveData) => void;
  logWarn: (msg: string, err?: unknown, extra?: PrimitiveData) => void;
  logInfo: (msg: string, extra?: PrimitiveData) => void;
  logTrack: (msg: string, extra?: PrimitiveData) => void;
} => ({
  logTrack: (msg: string, extra?: PrimitiveData) => {
    return Logger.track(msg, { ...preExtra, ...extra });
  },
  logInfo: (msg: string, extra?: PrimitiveData) => {
    return Logger.info(msg, { ...preExtra, ...extra });
  },
  logWarn: (msg: string, err?: unknown, extra?: PrimitiveData) => {
    return Logger.warn(msg, { ...preExtra, ...extra }, err);
  },
  logError: (msg: string, err?: unknown, extra?: PrimitiveData) => {
    return Logger.error(err, { ...preExtra, ...extra }, msg);
  },
});

const trackAction = (act: string, actContext?: object) => {
  if (ENABLE_DDOG_LOGS_ON_CONSOLE) {
    console.info('[DD] [ACTION]', act, actContext ?? {});
  }
  return dataDogAction(act, actContext);
};

export { filterNonEmptyTraits, getErrorMetadata, getLogger, getSafeError, isBenignError, trackAction };
export default Logger;
